import { MouseEvent as ReactMouseEvent, useState } from 'react';
import clsx from 'clsx';
import {
    MdMoreHoriz,
    MdOutlineDelete,
    MdOutlineMarkEmailRead,
    MdOutlineNotificationsOff,
    MdOutlineTune,
} from 'react-icons/md';

import { useMenuPick } from '@srm/ui/hooks/useMenuPick';
import Menu from '@srm/ui/menu';

import './NotificationItemMenu.scss';

type ItemAction = (e: ReactMouseEvent) => void;

export interface INotificationItemMenuProps {
    /** Документ можно заглушить (информационная категория, не назначенная работа) — доступно «Не уведомлять». */
    canMute: boolean;
    /** Метка категории — если задана, доступен пункт «Настроить «…»». */
    categoryLabel?: string | null;
    /** Уже прочитано — тогда пункт «Прочитать» скрыт. */
    isRead: boolean;
    disabled?: boolean;
    /** Класс кнопки-триггера — вписать в ряд действий айтема. */
    buttonClassName?: string;
    onMarkRead: ItemAction;
    onMute: ItemAction;
    onConfigure: ItemAction;
    onDelete: ItemAction;
    /** Открыто ли меню — хосту, чтобы удержать раскрытыми действия айтема. */
    onOpenChange?: (opened: boolean) => void;
}

type ItemMenuProps = Omit<INotificationItemMenuProps, 'disabled' | 'buttonClassName' | 'onOpenChange'>;

/** Пункты меню — своя вёрстка (иконка, опасное «Удалить», разделители); контейнер `role="menu"` и клавиатура — у
 *  `Menu` пакета. */
const ItemMenu = ({ canMute, categoryLabel, isRead, onMarkRead, onMute, onConfigure, onDelete }: ItemMenuProps) => {
    const pick = useMenuPick();

    return (
        <>
            {!isRead && (
                <button type="button" role="menuitem" className="nim__row" onClick={pick(onMarkRead)}>
                    <MdOutlineMarkEmailRead size="17" />
                    Прочитать
                </button>
            )}
            {!isRead && (canMute || categoryLabel) && <div className="nim__sep" role="separator" />}
            {canMute && (
                <button type="button" role="menuitem" className="nim__row" onClick={pick(onMute)}>
                    <MdOutlineNotificationsOff size="17" />
                    Не уведомлять по документу
                </button>
            )}
            {categoryLabel && (
                <button type="button" role="menuitem" className="nim__row" onClick={pick(onConfigure)}>
                    <MdOutlineTune size="17" />
                    Настроить «{categoryLabel}»
                </button>
            )}
            <div className="nim__sep" role="separator" />
            <button type="button" role="menuitem" className="nim__row nim__row--danger" onClick={pick(onDelete)}>
                <MdOutlineDelete size="17" />
                Удалить
            </button>
        </>
    );
};

/**
 * Меню «⋯» действий уведомления в дропдауне колокольчика (`Menu` пакета со своей вёрсткой пунктов). Консолидирует
 * редкие действия (прочитать, не уведомлять по документу, настроить категорию, удалить), оставляя на карточке только
 * частое «Открыть». Меню в портале — не обрезается скролл-контейнером ленты уведомлений.
 */
const NotificationItemMenu = ({ disabled, buttonClassName, onOpenChange, ...items }: INotificationItemMenuProps) => {
    const [opened, setOpened] = useState(false);

    // щелчки кнопки и пунктов (портал всплывает по дереву React) не доходят до строки-ссылки уведомления
    return (
        <div className="nim" onClick={(e) => e.stopPropagation()}>
            <Menu
                content={<ItemMenu {...items} />}
                contentRole="menu"
                opened={opened}
                onOpenChange={(next) => {
                    setOpened(next);
                    onOpenChange?.(next);
                }}
                disabled={disabled}
                position="bottom-end"
                popoverProps={{ className: 'nim__menu', listClassName: 'nim__list', minWidth: 238, verticalOffset: 6 }}
            >
                <button
                    type="button"
                    className={clsx('nim__trigger', buttonClassName, opened && 'nim__trigger--open')}
                    title="Ещё действия"
                    aria-label="Ещё действия"
                    disabled={disabled}
                    // строка уведомления — ссылка: щелчок по кнопке не переходит по ней
                    onClick={(e) => e.preventDefault()}
                >
                    <MdMoreHoriz size="18" />
                </button>
            </Menu>
        </div>
    );
};

export default NotificationItemMenu;
